import path from 'path';
import { UserConfig } from 'next-i18next';

import { AppLocale, AppFullLocale, AppMainLocale, AppSubLocale, ValueLabel } from '@/types';
import { NS, languages } from '@/constants';
import { promiseHelperAllSettled } from '@/utils/promiseUtils';
import i18n from '../../i18n.mjs';

/**
 * Load a date-fns locale
 * @param {AppLocale} locale The locale string
 * @return {object} The loaded date-fns locale
 */
export const loadLocale = async (locale: AppLocale = 'fr-FR'): Promise<object> => {
  let dl: AppMainLocale;

  if (!languages[locale]) {
    const key = Object.keys(languages).find(
      (lang) => languages[lang].includes(locale)
    ) as AppMainLocale | undefined;

    dl = key || 'fr';
  } else {
    dl = locale as AppMainLocale;
  }

  return import(`date-fns/locale/${dl}/index.js`).then((m) => m.default);
};

/**
 * Date picker localization
 * Load the right localized date string depending the current store locale
 * @param {AppLocale} locale Full locale string (short + sub)
 * @param {function} registerLocale the helper function provided by react-datepicker
 */
export const setLocalization = async (
  locale?: AppLocale, registerLocale?: (locale: AppLocale, data: any) => void
): Promise<void> => {
  if (locale) {
    const lng = await loadLocale(locale);
    registerLocale(locale, lng);
  }
};

export const getMainLocale = (locale?: AppFullLocale): AppMainLocale => (
  locale?.split('-')[0] || ''
) as AppMainLocale;

export const getSubLocale = (locale?: AppFullLocale): AppSubLocale => (
  locale?.split('-')[1] || ''
) as AppSubLocale;

export const getFormattedLocale = (locale: AppFullLocale): AppLocale => {
  if (languages.el.includes(locale)) {
    return languages.el[1] as AppLocale;
  }

  const mainLocale = getMainLocale(locale);
  const subLocale = getSubLocale(locale).toLowerCase();

  if (subLocale) {
    return mainLocale === subLocale ? subLocale : locale;
  }

  return mainLocale;
};

/**
 * Build an array with 3 elements:
 *   - favorites: a list of store countries items
 *   - others: a lis of all other countries items
 *   - init: the currently selected item
 * An item has two attributes:
 *   - the label
 *   - the value
 * @param {AppFullLocale} locale The current locale (xx-XX)
 * @param {*} selected The active country
 * @return {array} The 3 items
 */
export const formatCountriesList = async (
  locale: AppFullLocale, selected?: string
): Promise<[ValueLabel[], ValueLabel[], ValueLabel]> => {
  const selectedCountry = selected || getSubLocale(locale);
  const storeCountries = getStoreCountries();
  const countries = await mapCountries(locale);

  const favorites = countries.filter((country) => storeCountries.includes(country.value));
  const others = countries.filter((country) => !storeCountries.includes(country.value));

  const selectedItem = getCountryByCode(countries, selectedCountry);

  const init = {
    value: selectedCountry,
    label: selectedItem ? selectedItem.label : ''
  };

  return [favorites, others, init];
};

/**
 * Return the text corresponding to the given key-suffix or simply key if previous doesn't exist
 * @param {*} t The withTranslation t function
 * @param {*} key The base key
 * @param {*} suffix The key suffix
 * @return {string} The translated text
 */
export const contextTrad = (t: (key: string | string[]) => string, key: string | string[], suffix: string): string => {
  if (suffix) {
    const suffixKey = `${key}-${suffix}`;
    const trad = t(suffixKey);

    // i18next returns the key without namespace if missing
    if (trad !== suffixKey.split(':').pop()) {
      return trad;
    }
  }
  return t(key);
};

// Local helpers. Exported only for unit testing purpose
export const getCountriesFromLocale = async (locale) => {
  const results = await promiseHelperAllSettled([
    import(`../../public/static/locales/${locale.split('-')[0]}/countries.json`).then((module) => module.default) as any,
    import(`../../public/static/locales/${locale}/countries.json`).then((module) => module.default) as any
  ]);

  return results.reduce((acc, result) => {
    if (result.status === 'fulfilled') {
      return { ...acc, ...result['value'] };
    }
    return acc;
  }, {});
};

export const getStoreCountries = () => (
  [...new Set(Object.values(languages).reduce((list, values) => [
    ...list,
    ...values.map((v) => v.split('-')[1])
  ], []).filter(Boolean))]
);

export const mapCountries = (locale) => (
  getCountriesFromLocale(locale).then(
    (countries) => Object.entries(countries).map(([key, value]): ValueLabel => ({
      value: key,
      label: value as string
    }))
  )
);

export const getCountryByCode = (countries?: ValueLabel[], countryCode?: string) => (
  countries ? countries.find((country) => country.value === countryCode) : undefined
);

export const getI18NextConfigForLocale = (locale: string): UserConfig => ({
  i18n,
  react: {
    useSuspense: false
  },
  ns: [
    NS.COMMON,
    NS.CATEG,
    NS.CHECKOUT,
    NS.CELLAR,
    NS.CUSTOMIZATION,
    NS.ADMIN,
    NS.PRODUCT,
    NS.BLOG,
    NS.ALTER,
    NS.COUNTRIES
  ],
  reloadOnPrerender: process.env.NODE_ENV !== 'production',
  localePath: path.resolve('public/static/locales'),
  load: 'all',
  debug: process.env.NEXT_PUBLIC_I18N_DEBUG === 'true',
  fallbackLng: [
    locale,
    locale.split('-')[0],
    locale.split('-')[0] !== 'fr' && 'fr'
  ].filter(Boolean)
});
